import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input, Textarea } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { FormRow } from '../../components/ui/FormRow';
import { useVolver } from '../../lib/useVolver';

type Cliente = { id: string; codigo: string };
type Marca = { id: string; nombre: string; cliente_id: string };
type Categoria = { id: string; nombre: string };
type Gama = { id: string; nombre: string };
type Proveedor = { id: string; nombre: string };
type Estado = { codigo: string; nombre: string };

export function ProductoNuevo({ clienteCodigo }: { clienteCodigo?: string } = {}) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const clientes = useQuery({ queryKey: ['list', '/admin/clientes'], queryFn: () => api.get<{ data: Cliente[] }>('/admin/clientes') });
  const marcas = useQuery({ queryKey: ['list', '/admin/marcas'], queryFn: () => api.get<{ data: Marca[] }>('/admin/marcas') });
  const categorias = useQuery({ queryKey: ['list', '/admin/categorias'], queryFn: () => api.get<{ data: Categoria[] }>('/admin/categorias') });
  const gamas = useQuery({ queryKey: ['list', '/admin/gamas'], queryFn: () => api.get<{ data: Gama[] }>('/admin/gamas') });
  const proveedores = useQuery({ queryKey: ['list', '/proveedores'], queryFn: () => api.get<{ data: Proveedor[] }>('/proveedores') });
  const estados = useQuery({ queryKey: ['list', '/admin/producto-estados'], queryFn: () => api.get<{ data: Estado[] }>('/admin/producto-estados') });

  // Marcas visibles según el contexto del cliente. Si la URL nos da
  // clienteCodigo (p.ej. GAME en /game/productos/nuevo), filtramos para
  // no mezclar marcas de otros clientes en el dropdown.
  const marcasDelCliente = useMemo<Marca[]>(() => {
    const all = marcas.data?.data ?? [];
    if (!clienteCodigo) return all;
    const cli = (clientes.data?.data ?? []).find((c) => c.codigo === clienteCodigo);
    if (!cli) return [];
    return all.filter((m) => m.cliente_id === cli.id);
  }, [marcas.data, clientes.data, clienteCodigo]);

  const [form, setForm] = useState({
    sku: '',
    ean: '',
    nombre: '',
    marca_id: '',
    gama_id: '',
    categoria_id: '',
    proveedor_id: '',
    ref_proveedor: '',
    coste: '',
    pvpr: '',
    estado: 'EN_FABRICACION',
    notas: '',
  });

  // Auto-seleccionar la única marca cuando hay un sólo candidato (GAME).
  useEffect(() => {
    if (marcasDelCliente.length === 1 && form.marca_id !== marcasDelCliente[0].id) {
      setForm((f) => ({ ...f, marca_id: marcasDelCliente[0].id }));
    }
  }, [marcasDelCliente, form.marca_id]);

  const productoBase = clienteCodigo === 'GAME'
    ? '/game/productos'
    : clienteCodigo === 'TICNOVA'
      ? '/ticnova/productos'
      : '/productos';
  const volver = useVolver(productoBase);

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<{ id: string }>('/productos', body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['productos'] });
      navigate(`${productoBase}/${res.id}`);
    },
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.sku || !form.nombre || !form.marca_id) return;
    await create.mutateAsync({
      sku: form.sku,
      ean: form.ean || null,
      nombre: form.nombre,
      marca_id: form.marca_id,
      gama_id: form.gama_id || null,
      categoria_id: form.categoria_id || null,
      proveedor_id: form.proveedor_id || null,
      ref_proveedor: form.ref_proveedor || null,
      coste: form.coste === '' ? null : Number(form.coste),
      pvpr: form.pvpr === '' ? null : Number(form.pvpr),
      estado: form.estado || 'EN_FABRICACION',
      notas: form.notas || null,
    });
  };

  return (
    <div>
      <PageHeader
        title={clienteCodigo ? `Nuevo producto ${clienteCodigo}` : 'Nuevo producto'}
        subtitle="Da de alta un producto actual del cliente."
        actions={<Button variant="secondary" onClick={volver}>← Volver</Button>}
      />
      <form onSubmit={submit} className="max-w-4xl">
        <Card>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <FormRow label="SKU" required><Input value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} /></FormRow>
            <FormRow label="EAN"><Input value={form.ean} onChange={(e) => setForm((f) => ({ ...f, ean: e.target.value }))} /></FormRow>
            <FormRow label="Nombre" required><Input value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} /></FormRow>
            {marcasDelCliente.length === 1 ? null : (
              <FormRow label="Marca" required>
                <Select
                  options={marcasDelCliente.map((m) => ({ value: m.id, label: m.nombre }))}
                  value={form.marca_id}
                  onChange={(e) => setForm((f) => ({ ...f, marca_id: e.target.value }))}
                />
              </FormRow>
            )}
            <FormRow label="Gama" hint="Clasificación superior: Office / PC-Gaming / Sports / Lifestyle / Profesional / Console.">
              <Select
                options={(gamas.data?.data ?? []).map((g) => ({ value: g.id, label: g.nombre }))}
                value={form.gama_id}
                onChange={(e) => setForm((f) => ({ ...f, gama_id: e.target.value }))}
              />
            </FormRow>
            <FormRow label="Categoría">
              <Select
                options={(categorias.data?.data ?? []).map((c) => ({ value: c.id, label: c.nombre }))}
                value={form.categoria_id}
                onChange={(e) => setForm((f) => ({ ...f, categoria_id: e.target.value }))}
              />
            </FormRow>
            <FormRow label="Proveedor">
              <Select
                options={(proveedores.data?.data ?? []).map((p) => ({ value: p.id, label: p.nombre }))}
                value={form.proveedor_id}
                onChange={(e) => setForm((f) => ({ ...f, proveedor_id: e.target.value }))}
              />
            </FormRow>
            <FormRow label="Ref. proveedor"><Input value={form.ref_proveedor} onChange={(e) => setForm((f) => ({ ...f, ref_proveedor: e.target.value }))} /></FormRow>
            <FormRow label="Coste inicial sin IVA (EUR)"><Input type="number" step="any" value={form.coste} onChange={(e) => setForm((f) => ({ ...f, coste: e.target.value }))} /></FormRow>
            <FormRow label="PVPr inicial con IVA (EUR)"><Input type="number" step="any" value={form.pvpr} onChange={(e) => setForm((f) => ({ ...f, pvpr: e.target.value }))} /></FormRow>
            <FormRow label="Estado">
              <Select
                options={(estados.data?.data ?? []).map((e) => ({ value: e.codigo, label: e.nombre }))}
                value={form.estado}
                onChange={(e) => setForm((f) => ({ ...f, estado: e.target.value }))}
                emptyLabel={null}
              />
            </FormRow>
            <div className="sm:col-span-2 lg:col-span-3">
              <FormRow label="Notas"><Textarea value={form.notas} onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))} /></FormRow>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="danger" type="button" onClick={() => navigate(productoBase)}>Cancelar</Button>
            <Button variant="create" type="submit" disabled={create.isPending}>{create.isPending ? 'Creando…' : 'Crear producto'}</Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
